import { CommonModule } from '@angular/common';
import * as i0 from '@angular/core';
import { HostListener, Input, Directive, NgModule, Pipe, Injectable, forwardRef, Renderer2, ElementRef, Inject, HostBinding } from '@angular/core';
import * as i1 from '@windmill/ng-windmill/tooltip';
import { DefaultValueAccessor } from '@angular/forms';

class ToggleTooltipOnEllipsisDirective {
    elementRef;
    host;
    elementClassName;
    hasTooltip = false;
    constructor(elementRef, host) {
        this.elementRef = elementRef;
        this.host = host;
    }
    onMouseEnter() {
        this.showTooltip();
    }
    onMouseLeave() {
        this.hideTooltip();
    }
    handleKeyDown(event) {
        if (event.key === 'Tab' && event.target.attributes.getNamedItem('centric-toggle-tooltip-on-ellipsis')) {
            this.showTooltip();
        }
        else {
            this.hideTooltip();
        }
    }
    onBlur() {
        this.hideTooltip();
    }
    showTooltip() {
        if (!this.hasTooltip) {
            return;
        }
        let element = this.elementRef.nativeElement;
        if (this.elementClassName) {
            element = element.getElementsByClassName(this.elementClassName)[0];
        }
        this._updateTooltipProperties(element);
    }
    _updateTooltipProperties(element) {
        if (element && element.clientHeight < element.scrollHeight) {
            if (element.textContent) {
                this.host.tooltipText = element.textContent;
            }
            setTimeout(() => {
                this.host.isTooltipDisabled = false;
                this.host.show();
            }, this.host.tooltipShowDelay);
        }
    }
    hideTooltip() {
        if (!this.hasTooltip) {
            return;
        }
        setTimeout(() => {
            this.host.isTooltipDisabled = true;
            this.host.hide(this.host.tooltipHideDelay);
        }, this.host.tooltipHideDelay);
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.2.10", ngImport: i0, type: ToggleTooltipOnEllipsisDirective, deps: [{ token: i0.ElementRef }, { token: i1.WindmillTooltipDirective }], target: i0.ɵɵFactoryTarget.Directive });
    static ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "19.2.10", type: ToggleTooltipOnEllipsisDirective, isStandalone: true, selector: "[centric-toggle-tooltip-on-ellipsis]", inputs: { elementClassName: "elementClassName", hasTooltip: "hasTooltip" }, host: { listeners: { "mouseenter": "onMouseEnter()", "mouseleave": "onMouseLeave()", "keyup": "handleKeyDown($event)", "blur": "onBlur()" } }, ngImport: i0 });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.2.10", ngImport: i0, type: ToggleTooltipOnEllipsisDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[centric-toggle-tooltip-on-ellipsis]',
                    standalone: true
                }]
        }], ctorParameters: () => [{ type: i0.ElementRef }, { type: i1.WindmillTooltipDirective }], propDecorators: { elementClassName: [{
                type: Input
            }], hasTooltip: [{
                type: Input
            }], onMouseEnter: [{
                type: HostListener,
                args: ['mouseenter']
            }], onMouseLeave: [{
                type: HostListener,
                args: ['mouseleave']
            }], handleKeyDown: [{
                type: HostListener,
                args: ['keyup', ['$event']]
            }], onBlur: [{
                type: HostListener,
                args: ['blur']
            }] } });

const moduleComponents = [ToggleTooltipOnEllipsisDirective];
class DirectivesModule {
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.2.10", ngImport: i0, type: DirectivesModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
    static ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "19.2.10", ngImport: i0, type: DirectivesModule, imports: [CommonModule, ToggleTooltipOnEllipsisDirective], exports: [ToggleTooltipOnEllipsisDirective] });
    static ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "19.2.10", ngImport: i0, type: DirectivesModule, imports: [CommonModule] });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.2.10", ngImport: i0, type: DirectivesModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [CommonModule, ...moduleComponents],
                    exports: [...moduleComponents]
                }]
        }] });

class CentricFirstLetterUppercasePipe {
    transform(value) {
        return value ? value[0].toUpperCase() + value.slice(1) : '';
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.2.10", ngImport: i0, type: CentricFirstLetterUppercasePipe, deps: [], target: i0.ɵɵFactoryTarget.Pipe });
    static ɵpipe = i0.ɵɵngDeclarePipe({ minVersion: "14.0.0", version: "19.2.10", ngImport: i0, type: CentricFirstLetterUppercasePipe, isStandalone: true, name: "firstLetterUppercase" });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.2.10", ngImport: i0, type: CentricFirstLetterUppercasePipe, decorators: [{
            type: Pipe,
            args: [{
                    name: 'firstLetterUppercase',
                    standalone: true
                }]
        }] });

const SMALL_MAX = 600;
const MEDIUM_MAX = 1200;
const LARGE_MAX = 1920;
class ResponsiveService {
    /** Media presets that can be used with Angular CDK's BreakpointObserver or MediaMatcher.
     * Similar to CSS media queries
     */
    breakpoints = {
        small: `(max-width: ${SMALL_MAX - 1}px)`,
        medium: `(min-width: ${SMALL_MAX}px) and (max-width: ${MEDIUM_MAX - 1}px)`,
        large: `(min-width: ${MEDIUM_MAX}px) and (max-width: ${LARGE_MAX - 1}px)`,
        xLarge: `(min-width: ${LARGE_MAX}px)`
    };
    /**
     * Detects if a width belongs to the "small" breakpoint.
     * Used in combination with a ResizeObserver, can behave like CSS container queries
     */
    isSmall(width) {
        return width < SMALL_MAX;
    }
    /**
     * Detects if a width belongs to the "medium" breakpoint.
     * Used in combination with a ResizeObserver, can behave like CSS container queries
     */
    isMedium(width) {
        return SMALL_MAX <= width && width < MEDIUM_MAX;
    }
    /**
     * Detects if a width belongs to the "large" breakpoint.
     * Used in combination with a ResizeObserver, can behave like CSS container queries
     */
    isLarge(width) {
        return MEDIUM_MAX <= width && width < LARGE_MAX;
    }
    /**
     * Detects if a width belongs to the "x-large" breakpoint.
     * Used in combination with a ResizeObserver, can behave like CSS container queries
     */
    isXLarge(width) {
        return width >= LARGE_MAX;
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.2.10", ngImport: i0, type: ResponsiveService, deps: [], target: i0.ɵɵFactoryTarget.Injectable });
    static ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "19.2.10", ngImport: i0, type: ResponsiveService, providedIn: 'root' });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.2.10", ngImport: i0, type: ResponsiveService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }] });

class PageProperties {
    pageIndex;
    previousPageIndex;
    pageSize;
    length;
}

class InputSharedPropertiesBase extends DefaultValueAccessor {
    /**
     * Placeholder text for component
     */
    placeholder;
    /**
     * Size of the component
     */
    size;
    /**
     *  Title text of the component
     */
    title;
    /**
     * The id for input
     */
    id;
    /**
     *  Data-testid attribute for component
     */
    testInputId;
    /**
     * If true, the component will have a clear icon
     */
    hasClearIcon = false;
    /**
     * Whether the component is readonly
     */
    readonly = false;
    /**
     * Could be used for adding a custom function for validation for errors of component.
     */
    validationFunction;
    /**
     * Sets the aria-label attribute for the input
     */
    ariaLabel;
    /**
     * Sets the aria-description attribute for the input
     */
    ariaDescription;
    /**
     * The aria-labelledby attribute which will be forwarded to the input element
     */
    ariaLabelledby;
    /**
     * The aria-describedby attribute which is read after the element's label and field type
     */
    ariaDescribedby;
    /**
     * The aria-label value for clear icon
     */
    ariaLabelClearIcon;
    disabled = false;
    constructor(render2, elementRef) {
        super(render2, elementRef, false);
    }
    registerOnChange(fn) {
        this.onChange = fn;
    }
    registerOnTouched(fn) {
        this.onTouched = fn;
    }
    setDisabledState(isDisabled) {
        this.disabled = isDisabled;
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.2.10", ngImport: i0, type: InputSharedPropertiesBase, deps: [{ token: forwardRef(() => Renderer2) }, { token: forwardRef(() => ElementRef) }], target: i0.ɵɵFactoryTarget.Directive });
    static ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "19.2.10", type: InputSharedPropertiesBase, isStandalone: true, inputs: { placeholder: "placeholder", size: "size", title: "title", id: "id", testInputId: "testInputId", hasClearIcon: "hasClearIcon", readonly: "readonly", validationFunction: "validationFunction", ariaLabel: "ariaLabel", ariaDescription: "ariaDescription", ariaLabelledby: "ariaLabelledby", ariaDescribedby: "ariaDescribedby", ariaLabelClearIcon: "ariaLabelClearIcon" }, usesInheritance: true, ngImport: i0 });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.2.10", ngImport: i0, type: InputSharedPropertiesBase, decorators: [{
            type: Directive
        }], ctorParameters: () => [{ type: i0.Renderer2, decorators: [{
                    type: Inject,
                    args: [forwardRef(() => Renderer2)]
                }] }, { type: i0.ElementRef, decorators: [{
                    type: Inject,
                    args: [forwardRef(() => ElementRef)]
                }] }], propDecorators: { placeholder: [{
                type: Input
            }], size: [{
                type: Input
            }], title: [{
                type: Input
            }], id: [{
                type: Input
            }], testInputId: [{
                type: Input
            }], hasClearIcon: [{
                type: Input
            }], readonly: [{
                type: Input
            }], validationFunction: [{
                type: Input
            }], ariaLabel: [{
                type: Input
            }], ariaDescription: [{
                type: Input
            }], ariaLabelledby: [{
                type: Input
            }], ariaDescribedby: [{
                type: Input
            }], ariaLabelClearIcon: [{
                type: Input
            }] } });

class MaskSharedPropertiesBase extends InputSharedPropertiesBase {
    /**
     * Mask value for component
     */
    mask;
    /**
     * You can validate your formControl. The default value is true.
     */
    validation = true;
    /**
     * List with special characters which are allowed
     */
    specialCharacters = ['-', '/', '(', ')', '.', ':', ' ', '+', ',', '@', '[', ']', '"', "'"];
    /**
     * The patterns of the mask
     */
    patterns;
    /**
     * You can add prefix to you masked value
     */
    prefix = '';
    /**
     * You can add suffix to you masked value
     */
    suffix = '';
    /**
     * You can choose if mask will drop special character in the model, or not. The default value is true.
     */
    dropSpecialCharacters = true;
    /**
     * You can choose if the mask is shown while typing, or not. The default value is false.
     */
    showMaskTyped = false;
    /**
     * You can choose if mask will allow the use of negative numbers. The default value is false.
     */
    allowNegativeNumbers = false;
    /**
     * If the showMaskTyped parameter is enabled, this setting customizes the character used as placeholder. The default value is _.
     */
    placeHolderCharacter = '_';
    /**
     * You can choose clear the input if the input value not match the mask. The default value is false.
     */
    clearIfNotMatch = false;
    /**
     * You can divide your input by thousands. By default will separate with a space.
     */
    thousandSeparator = ' ';
    /**
     * You can choose your decimal marker, comma or dot. The default value is dot.
     */
    decimalMarker = '.';
    /**
     * You can limit the number of digits before the decimal point
     */
    separatorLimit = '';
    /**
     * You can hide symbols in input field and get the actual value in form control.
     */
    hiddenInput;
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.2.10", ngImport: i0, type: MaskSharedPropertiesBase, deps: null, target: i0.ɵɵFactoryTarget.Directive });
    static ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "19.2.10", type: MaskSharedPropertiesBase, isStandalone: true, inputs: { mask: "mask", validation: "validation", specialCharacters: "specialCharacters", patterns: "patterns", prefix: "prefix", suffix: "suffix", dropSpecialCharacters: "dropSpecialCharacters", showMaskTyped: "showMaskTyped", allowNegativeNumbers: "allowNegativeNumbers", placeHolderCharacter: "placeHolderCharacter", clearIfNotMatch: "clearIfNotMatch", thousandSeparator: "thousandSeparator", decimalMarker: "decimalMarker", separatorLimit: "separatorLimit", hiddenInput: "hiddenInput" }, usesInheritance: true, ngImport: i0 });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.2.10", ngImport: i0, type: MaskSharedPropertiesBase, decorators: [{
            type: Directive
        }], propDecorators: { mask: [{
                type: Input
            }], validation: [{
                type: Input
            }], specialCharacters: [{
                type: Input
            }], patterns: [{
                type: Input
            }], prefix: [{
                type: Input
            }], suffix: [{
                type: Input
            }], dropSpecialCharacters: [{
                type: Input
            }], showMaskTyped: [{
                type: Input
            }], allowNegativeNumbers: [{
                type: Input
            }], placeHolderCharacter: [{
                type: Input
            }], clearIfNotMatch: [{
                type: Input
            }], thousandSeparator: [{
                type: Input
            }], decimalMarker: [{
                type: Input
            }], separatorLimit: [{
                type: Input
            }], hiddenInput: [{
                type: Input
            }] } });

class ActionBaseDirective {
    /**
     * Property that sets the type of the button/link from a design perspective
     */
    type = 'high-emphasis-theme';
    /**
     * TabIndex for special cases in which we want the focus outline on the button/link wrapper, not on the button/link
     */
    tabIndex;
    /**
     * Button/link size
     */
    size;
    /**
     * Button/link icon
     */
    iconId;
    /**
     * Button/link icon size
     */
    iconsize;
    /**
     * Aria label for button/link
     */
    ariaLabel;
    /**
     * Aria labelledby for button/link
     */
    ariaLabelledby;
    /**
     * Aria description for button/link
     */
    ariaDescription;
    /**
     * Aria describedby for button/link
     */
    ariaDescribedby;
    componentClasses;
    get buttonClass() {
        if (this.size) {
            return `${this.type} ${this.size}`;
        }
        return this.type;
    }
    get determineIconSize() {
        if (!this.iconsize) {
            if (this.size === 'small') {
                return '20';
            }
            return '24';
        }
        return this.iconsize;
    }
    ngOnChanges() {
        this.updateComponentClass();
    }
    updateComponentClass() {
        const newClasses = [];
        newClasses.push(this.type);
        if (this.size) {
            newClasses.push(String(this.size));
        }
        this.componentClasses = newClasses.join(' ');
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.2.10", ngImport: i0, type: ActionBaseDirective, deps: [], target: i0.ɵɵFactoryTarget.Directive });
    static ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "19.2.10", type: ActionBaseDirective, isStandalone: true, inputs: { type: "type", tabIndex: "tabIndex", size: "size", iconId: "iconId", iconsize: "iconsize", ariaLabel: "ariaLabel", ariaLabelledby: "ariaLabelledby", ariaDescription: "ariaDescription", ariaDescribedby: "ariaDescribedby" }, host: { properties: { "class": "this.componentClasses" } }, usesOnChanges: true, ngImport: i0 });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.2.10", ngImport: i0, type: ActionBaseDirective, decorators: [{
            type: Directive
        }], propDecorators: { type: [{
                type: Input
            }], tabIndex: [{
                type: Input
            }], size: [{
                type: Input
            }], iconId: [{
                type: Input
            }], iconsize: [{
                type: Input
            }], ariaLabel: [{
                type: Input
            }], ariaLabelledby: [{
                type: Input
            }], ariaDescription: [{
                type: Input
            }], ariaDescribedby: [{
                type: Input
            }], componentClasses: [{
                type: HostBinding,
                args: ['class']
            }] } });

var CentricThemeA11yLevel;
(function (CentricThemeA11yLevel) {
    CentricThemeA11yLevel["aa"] = "AA";
    CentricThemeA11yLevel["aaa"] = "AAA";
})(CentricThemeA11yLevel || (CentricThemeA11yLevel = {}));

var CentricThemeColor;
(function (CentricThemeColor) {
    CentricThemeColor["neutral"] = "neutral";
    CentricThemeColor["green"] = "green";
    CentricThemeColor["blue"] = "blue";
    CentricThemeColor["purple"] = "purple";
})(CentricThemeColor || (CentricThemeColor = {}));

var CentricThemeVariant;
(function (CentricThemeVariant) {
    CentricThemeVariant["light"] = "light";
    CentricThemeVariant["dark"] = "dark";
})(CentricThemeVariant || (CentricThemeVariant = {}));

class CentricSharedModule {
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.2.10", ngImport: i0, type: CentricSharedModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
    static ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "19.2.10", ngImport: i0, type: CentricSharedModule, imports: [CommonModule, CentricFirstLetterUppercasePipe], exports: [CentricFirstLetterUppercasePipe] });
    static ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "19.2.10", ngImport: i0, type: CentricSharedModule, imports: [CommonModule] });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.2.10", ngImport: i0, type: CentricSharedModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [CommonModule, CentricFirstLetterUppercasePipe],
                    exports: [CentricFirstLetterUppercasePipe]
                }]
        }] });

/**
 * Generated bundle index. Do not edit.
 */

export { ActionBaseDirective, CentricFirstLetterUppercasePipe, CentricSharedModule, CentricThemeA11yLevel, CentricThemeColor, CentricThemeVariant, DirectivesModule, InputSharedPropertiesBase, MaskSharedPropertiesBase, PageProperties, ResponsiveService, ToggleTooltipOnEllipsisDirective };
